
import { Component, inject, OnDestroy, OnInit, signal } from '@angular/core';
import { RouterModule, Routes } from '@angular/router';
import { loginRoutes } from '@softapps/allgemein/login';
import { moduleRoutes } from '@softapps/allgemein/modules';
import { passwordRoutes } from '@softapps/allgemein/password';
import { settingsRoutes } from '@softapps/allgemein/utils';
import { terminalGuard } from '@softapps/core';
import { pmsStempelTerminalRoutes } from '@softapps/pms/stempel-terminal';
import { showRequestErrors } from '@softline/application';
import {
  AuthenticationContextStore,
  AuthenticationGuard,
  JwtAuthenticationStore,
  LogoutService,
  SOFTLINE_FEATURE_AUTHENTICATION_CONTEXT,
  SOFTLINE_FEATURE_JWT_AUTHENTICATION,
} from '@softline/auth';
import { ConnectionError, Store, UnauthorizedError } from '@softline/core';
import { MessageBarService } from '@softline/ui-core';
import {
  delay,
  distinctUntilChanged,
  interval,
  map,
  of,
  startWith,
  Subscription,
  switchMap,
} from 'rxjs';
import { storageLocationRoutes } from '@softapps/mde/storage-location';

@Component({
  selector: 'soft-terminal-auto-login',
  template: '<router-outlet></router-outlet>',
  styles: [],
  providers: [],
  imports: [RouterModule],
})
export class TerminalAutoLoginComponent implements OnInit, OnDestroy {
  private readonly store = inject(Store);
  private readonly messageBarService = inject(MessageBarService);
  private readonly logoutService = inject(LogoutService);

  loading = signal(false);

  private subscription?: Subscription;

  async ngOnInit(): Promise<void> {
    this.subscription = this.store
      .observe(
        SOFTLINE_FEATURE_AUTHENTICATION_CONTEXT,
        AuthenticationContextStore.getters.data
      )
      .pipe(
        distinctUntilChanged(
          (a, b) => (a as any)?.['username'] === (b as any)?.['username']
        ),
        switchMap((o: any) => {
          if (!!o && !!o['username']) {
            return of(null);
          }

          const user = {
            username: '125time01',
            password: 'Refi!Ufa47',
          };

          return interval(5000).pipe(
            startWith(0),
            map(() => user),
            delay(100)
          );
        })
      )
      .subscribe(async (o) => {
        if (!o) return;

        console.debug(
          '[TerminalAutoLoginComponent]: Logout occurred -> automatic login with user: ',
          o?.username
        );

        try {
          this.loading.set(true);

          await this.store.dispatch(
            SOFTLINE_FEATURE_JWT_AUTHENTICATION,
            JwtAuthenticationStore.actions.authenticate,
            { ...o, stayAuthenticated: true }
          );
        } catch (e) {
          if (e instanceof UnauthorizedError) {
            console.debug(
              '[TerminalAutoLoginComponent]: Auto Login failed due to being unauthorized',
              e
            );
            this.messageBarService.warning('#LOGIN.MESSAGES.FAILED.UNAUTHORIZED');
          } else if (e instanceof ConnectionError) {
            console.debug(
              '[TerminalAutoLoginComponent]: Auto Login failed due to a bad connection',
              e
            );
            this.messageBarService.warning('#LOGIN.MESSAGES.FAILED.NO_CONNECTION');
          } else {
            console.debug('[TerminalAutoLoginComponent]: Auto Login failed due to:', e);
            showRequestErrors(this.store, e);
          }
        } finally {
          this.loading.set(false);
        }
      });
  }

  ngOnDestroy() {
    if (this.subscription) {
      this.subscription.unsubscribe();
      this.subscription = undefined;
    }
  }
}

export const routes: Routes = [
  {
    path: 'terminal',
    loadComponent: () => import('@softapps/core').then((o) => o.TerminalComponent),
    loadChildren: () => [
      {
        // TODO: Remove when authentication via access token works -> this is an auto login workaround...
        path: '',
        component: TerminalAutoLoginComponent,
        children: [...pmsStempelTerminalRoutes],
      },
    ],
  },
  {
    path: '',
    loadComponent: () => import('@softapps/core').then((o) => o.ShellComponent),
    children: [
      //Allgemein
      ...moduleRoutes,
      ...settingsRoutes,

      ...pmsStempelTerminalRoutes,
      ...storageLocationRoutes
    ],
    canActivate: [AuthenticationGuard, terminalGuard],
  },
  {
    path: '',
    loadComponent: () => import('@softapps/core').then((o) => o.PublicShellComponent),
    children: [...loginRoutes, ...passwordRoutes, ...settingsRoutes],
    canActivate: [terminalGuard],
  },
  {
    path: '**',
    redirectTo: '/',
  },
];
